/**
 * Cemetery registry.
 *
 * To add a cemetery, append an entry here. Everything downstream — search,
 * the detail page, the interactive site map, pricing and the admin console —
 * is generated from this config. No component changes are required.
 *
 * Row letters run bottom-to-top on the map (rows[0] is the BOTTOM row, drawn
 * last), and plot numbers run left-to-right within a row.
 */

export const BURIAL_TYPES = {
  burial: { id: 'burial', label: 'Burial', short: 'Burial' },
  cremation: { id: 'cremation', label: 'Cremation', short: 'Cremation' },
};

const trinity = {
  id: 'trinity-church',
  name: 'Trinity Church Cemetery',
  tagline: 'A historic churchyard in Upper Marlboro, thoughtfully expanded.',
  established: 1810,
  address: {
    street: '14515 Church Street',
    city: 'Upper Marlboro',
    state: 'MD',
    zip: '20772',
    county: 'Prince George\'s County',
  },
  // Used by the search page for "near me" / location matching.
  coordinates: { lat: 38.8159, lng: -76.7497 },
  contact: {
    phone: '(301) 627-3100',
    email: 'woollsjack@cemeteryburialsolutions.org',
  },
  hours: 'Grounds open daily, dawn to dusk. Office by appointment.',
  acreage: 6.4,
  description:
    'Trinity Church Cemetery sits on a gently sloping parcel off Old Marlboro Pike, bordered by mature hardwoods and a vegetated swale. The proposed gravesite layout adds 586 plots across fourteen rows, an open space with a bell tower, and a dedicated cremation garden along the southern edge. Paths are laid out to allow excavating equipment access without disturbing neighboring sites.',
  highlights: [
    'Fourteen rows of 6×8 gravesites, laid out by Site Design Inc.',
    'Dedicated cremation garden along the southern boundary',
    'Proposed bell tower and open space at the heart of the grounds',
    'Evergreen privacy screening along the residential boundary',
    'Native plantings: American Holly, Eastern Redbud, Red Maple, Willow Oak',
    'Flat area reserved for future mausoleum construction',
  ],
  images: [
    {
      src: '/images/cemeteries/trinity/site-plan.png',
      alt: 'Trinity Church Cemetery proposed gravesite layout, surveyed site plan',
      caption: 'Proposed gravesite layout — Site Design Inc., 2025',
    },
    {
      src: '/images/cemeteries/trinity/site-plan-marked.png',
      alt: 'Trinity Church Cemetery site plan with rows lettered A through N and the cremation garden highlighted',
      caption: 'Rows A–N with the cremation garden highlighted',
    },
    {
      src: '/images/home/cemetery-types.jpg',
      alt: 'Mature trees lining a cemetery lawn',
      caption: 'Mature hardwoods along the northern boundary',
    },
    {
      src: '/images/home/cremation-gardens.jpg',
      alt: 'A cremation memorial garden',
      caption: 'The cremation garden, rows A and B',
    },
  ],

  /**
   * Grid geometry. Rows are listed bottom-to-top to match the surveyed plan:
   * row A is the southernmost row, row N the northernmost.
   */
  layout: {
    rows: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'],
    plotsPerRow: 30,
    // Rows shaded pink on the marked-up survey — the cremation garden.
    cremationRows: ['A', 'B'],
    // Visual breaks drawn on the map. A path is rendered ABOVE the named row.
    pathsAbove: ['B', 'G', 'K'],
  },

  pricing: {
    burial: 4500,
    cremation: 2200,
    // Optional per-row overrides, e.g. { N: 5200 } for a premium row.
    rowOverrides: {},
    // Shown at checkout.
    careFeeRate: 0.1, // perpetual care, 10% of site price
  },
};

export const CEMETERIES = [trinity];

export const getCemetery = (id) => CEMETERIES.find((c) => c.id === id) || null;

/** "Upper Marlboro, MD 20772" */
export const formatLocation = (cemetery) => {
  const { city, state, zip } = cemetery.address;
  return `${city}, ${state} ${zip}`;
};

/** Which burial type does a given row hold? */
export const rowBurialType = (cemetery, row) =>
  cemetery.layout.cremationRows.includes(row) ? 'cremation' : 'burial';

/** Price of a single site, honoring per-row overrides. */
export const plotPrice = (cemetery, row) => {
  const { rowOverrides, burial, cremation } = cemetery.pricing;
  if (rowOverrides[row] != null) return rowOverrides[row];
  return rowBurialType(cemetery, row) === 'cremation' ? cremation : burial;
};

/** Every plot id in the cemetery, e.g. "A-1" ... "N-30". */
export const allPlotIds = (cemetery) => {
  const ids = [];
  for (const row of cemetery.layout.rows) {
    for (let n = 1; n <= cemetery.layout.plotsPerRow; n += 1) {
      ids.push(`${row}-${n}`);
    }
  }
  return ids;
};

export const parsePlotId = (plotId) => {
  const [row, number] = plotId.split('-');
  return { row, number: Number(number) };
};

/** Amounts are whole dollars throughout. */
export const formatMoney = (amount) =>
  amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
